import { h } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { isLauraCanada } from 'utils/helpers';
import useResponsive from 'utils/responsive';
import clsx from 'clsx';

const ProductTabs = ({ currentProduct, delivery }) => {
  const t = window.TMW.Strings.product;
  const { benefits: benefitsList, masterSKU, descriptionHtml } = currentProduct;
  const [activeTab, setActiveTab] = useState('description');
  const [descriptionState, setDescriptionState] = useState(0);
  const descriptionContainer = useRef(null);
  const { isMobile } = useResponsive();

  const { description } = useMemo(() => {
    const payload = {
      styleNumber: `${t.styleNumber}: ${masterSKU?.value}`,
      description: descriptionHtml,
    };
    if (currentProduct.fabric?.value) {
      payload.description += `<p><strong>${t.materials}:</strong><br/>
        ${currentProduct.fabric.value.replaceAll('\n', '<br/>')}</p>`;
    }
    if (
      currentProduct.careDescription?.value &&
      currentProduct.careDescription?.value !== null &&
      currentProduct.careDescription?.value !== 'null'
    ) {
      const lines = JSON.parse(currentProduct.careDescription.value);
      payload.description += `<p><strong>${t.care}:</strong><br/>
            ${lines.join(' ')}</p>`;
    }
    if (isLauraCanada()) {
      payload.styleNumber = `${t.styleNumber} #${masterSKU?.value}`;
    }
    payload.description = payload.description.replace(
      /(<div[^>]+>|<meta[^>]+>|<div>|<\/div>)/g,
      '',
    );
    return payload;
  }, [currentProduct]);
  const benefits = benefitsList.filter((item) => item.name && item.icon);

  useEffect(() => {
    setActiveTab(
      description.length || benefits?.length ? 'description' : 'delivery',
    );
  }, [currentProduct]);

  useEffect(() => {
    if (descriptionState === 0 && descriptionContainer.current) {
      const isOverflowing =
        descriptionContainer.current.scrollHeight >
        descriptionContainer.current.clientHeight;
      setDescriptionState(isOverflowing ? (isMobile ? 1 : 2) : 0);
    }
  }, [descriptionState, isMobile]);

  useEffect(() => {
    if (descriptionContainer.current) {
      const isOverflowing =
        descriptionContainer.current.scrollHeight >
        descriptionContainer.current.clientHeight;
      setDescriptionState(isOverflowing ? (isMobile ? 1 : 2) : 0);
    }
  }, [isMobile]);

  if (!description.length && !benefits?.length && !delivery.length) {
    return null;
  }

  return (
    <div>
      <div
        className="flex gap-30 justify-start md:gap-40 md: py-24 pb-0 ml:justify-center"
        role="tablist"
        aria-orientation="horizontal"
      >
        {!!(description.length || benefits?.length) && (
          <button
            onClick={() => setActiveTab('description')}
            role="tab"
            aria-selected={activeTab === 'description'}
            id="desription-tab"
            aria-controls="desription-panel"
            className={clsx(
              'pb-8 body-2-medium border-b hover:text-black hover:border-black',
              {
                'border-black': activeTab === 'description',
                'text-subdued border-transparent': activeTab !== 'description',
              },
            )}
          >
            {t.descriptionLabel}
          </button>
        )}
        {!!delivery.length && (
          <button
            role="tab"
            id="delivery-tab"
            aria-selected={activeTab === 'delivery'}
            aria-controls="delivery-panel"
            onClick={() => setActiveTab('delivery')}
            className={clsx(
              'pb-8 body-2-medium border-b hover:text-black hover:border-black',
              {
                'border-black': activeTab === 'delivery',
                'text-subdued border-transparent': activeTab !== 'delivery',
              },
            )}
          >
            {t.deliveryLabel}
          </button>
        )}
      </div>
      {!!delivery.length && (
        <div
          id="delivery-panel"
          role="tabpanel"
          aria-labelledby="delivery-tab"
          className={`py-24 ml:text-center ${
            activeTab === 'delivery' ? '' : 'hidden'
          }`}
        >
          <div
            className="product-description [&>p]:m-0"
            data-target="text-content"
            dangerouslySetInnerHTML={{ __html: delivery }}
          ></div>
        </div>
      )}
      {!!(description.length || benefits?.length) && (
        <div
          id="description-panel"
          role="tabpanel"
          aria-labelledby="description-tab"
          className={`py-24 ml:text-center ${
            activeTab === 'description' ? '' : 'hidden'
          }`}
        >
          {description ? (
            <div
              className={`product-description ${
                descriptionState !== 2
                  ? 'line-clamp-2 h-[38.39px] overflow-hidden'
                  : ''
              }`}
              data-target="text-content"
              ref={descriptionContainer}
              dangerouslySetInnerHTML={{ __html: description }}
            ></div>
          ) : null}
          {descriptionState === 1 && (
            <button
              className="body-3-underline block hover:no-underline ml:mx-auto"
              onClick={() => setDescriptionState(2)}
              dangerouslySetInnerHTML={{
                __html: window.TMW.Strings.general.readMore,
              }}
            ></button>
          )}
          {descriptionState === 2 && (
            <button
              className="body-3-underline block hover:no-underline ml:mx-auto"
              onClick={() => setDescriptionState(1)}
              dangerouslySetInnerHTML={{
                __html: window.TMW.Strings.general.readLess,
              }}
            ></button>
          )}
          {!!benefits?.length && (
            <div className="grid grid-cols-3 mt-32 items-start">
              {benefits.slice(0, 3).map((benefit, index) => (
                <div
                  key={index}
                  className="flex flex-col justify-center items-center text-center"
                >
                  <img
                    className="w-30 h-30 ml:w-24 ml:h-24 object-cover block mb-4"
                    src={benefit.icon.reference.image?.src}
                    alt={benefit.name.value}
                  />
                  <div className="w-80 body-3">{benefit.name.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductTabs;
